/**
 * The hero house's back garden, measured off a photograph taken from the deck.
 *
 * Looking down the garden from the house (the camera's view in the reference),
 * +X is screen-right and -Z runs away toward the rear fence:
 *
 *      z -11.5  house wall / deck
 *      z -16    patio and deck steps give way to lawn
 *      z -27    lawn ends
 *      z -28.5  shed, against the planted back border
 *      z -30    rear fence  (built by fences.js -- do not rebuild it)
 *
 * The deck sits against the house on the +X half; the flagstone patio is at
 * grade beside and in front of it; a big flat lawn of freshly-laid sod fills
 * the middle; the shed sits at the far end against a screen of conifers.
 */

import { LOT_EDGES, LOTS, HERO_LOT, Z } from "../spec.js";

/**
 * The lot's own boundaries -- asked for, never remembered.
 *
 * All four were typed out: -7.5, 7.5, -11.5, -30.  Then spec.js tightened the
 * subdivision (lot spacing went from house width + 3 m to + 2 m) and fences.js
 * followed it, because fences.js reads LOT_EDGES.  This file did not, so for a
 * while the hero's back garden was measured off side boundaries three quarters
 * of a metre outside the fences actually built there, and everything datumed
 * on them -- the side bed, its mulch, and every plant on the screen -- stood
 * in the neighbours' gardens.  The two z's happened to still agree; they are
 * read from the spec now so that stays true by construction rather than by
 * luck.
 *
 * Anything that is the street's business is read from the street's spec.  The
 * numbers in the comments are what they evaluate to today; if they ever stop
 * matching, it is the comment that is wrong.
 */
export const LOT_MIN_X = LOT_EDGES[HERO_LOT];        // -6.75
export const LOT_MAX_X = LOT_EDGES[HERO_LOT + 1];    //  6.75
export const HOUSE_BACK = Z.houseBack;               // -11.5
export const FENCE_Z = Z.backFence;                  // -30

/**
 * Where planting may stand, given the crown it has to keep out of the boards.
 *
 * A boundary fence is not a line: fences.js lays each run a collider COL_T =
 * 0.16 m thick centred on the lot edge, so the face the cat (and the eye)
 * meets is FENCE_FACE inside the edge.  A plant clears it when its whole
 * crown clears it, which is why `crown` is a parameter -- the mistake this
 * replaces was a screen authored by trunk position, whose trunks were inside
 * the old line and whose foliage was half a metre through the new one.
 *
 * LEAF_GAP on top of that is pure looks: a crown that ends exactly on the
 * boards reads as growing out of them, and the boards move (the rear run is
 * being rebuilt as we speak).  Six centimetres is enough to see daylight.
 *
 * `plantLineX(sign, crown)` is the closest a plant of that size may sit to
 * the -X (sign -1) or +X (sign +1) boundary -- lay a screen ON it.
 * `insideBoundary(x, crown)` keeps an authored position, only pulling it back
 * if it would cross -- clamp individual specimens with it, the way spec.js's
 * insideFront clamps front-garden planting off the pavement.
 */
const FENCE_FACE = 0.08;   // fences.js COL_T / 2; private there, so mirrored here
const LEAF_GAP = 0.06;     // daylight between crown and boards

export const plantLineX = (sign, crown = 0) =>
  (sign < 0 ? LOT_MIN_X : LOT_MAX_X) - sign * (FENCE_FACE + LEAF_GAP + crown);

export const insideBoundary = (x, crown = 0) =>
  Math.min(Math.max(x, plantLineX(-1, crown)), plantLineX(1, crown));

/**
 * Raised timber deck off the back of the house.
 *
 * Heights here are above the garden's own ground, which the level puts one
 * terrace (spec.GRADE) below the street: `top` is 1.35 m over the lawn, as
 * photographed, and lands exactly level with the kitchen floor inside.
 */
export const DECK = {
  minX: 0.55,
  maxX: 5.60,
  minZ: -15.45,
  maxZ: HOUSE_BACK,
  top: 1.35,            // kitchen level, as in the photo -- and literally so:
                        // the patio door's sill is this, one terrace down
  railTop: 2.32,        // black metal balustrade on timber posts
  stepMinX: 0.55,       // the flight down into the patio, at the -X end
  stepMaxX: 2.45,
  steps: 7,             // 1.35 m in 7 risers of 0.193
};

/** Irregular flagstone at grade, beside and in front of the deck. */
export const PATIO = {
  minX: -4.30,
  maxX: 5.60,
  minZ: -18.10,
  maxZ: HOUSE_BACK,
};

/** Freshly laid sod -- the seams between rolls are clearly visible. */
export const LAWN = {
  minX: -6.60,
  maxX: 6.60,
  minZ: -27.60,
  maxZ: -16.40,
  rollWidth: 1.62,      // sod comes off the roll about this wide
  rollLength: 3.30,
};

/** The pale blue shed with white trim at the end of the garden. */
export const SHED = {
  x: -0.90,
  z: -28.35,
  width: 3.05,
  depth: 2.45,
  eave: 2.10,
  ridge: 2.95,
  padOversize: 0.45,    // gravel pad showing round the base
};

/**
 * Mulched bed with curved paver edging down the -X side.
 *
 * `minX` is the boundary plus BED_INSET, so the mulch stops just short of the
 * boards instead of running under them into lot 0 -- which is what it did
 * while LOT_MIN_X read -7.5.
 *
 * The depths are to the lawn-side edge, measured from `minX`, and they were
 * re-datumed when that moved: `depthNear` lost the same 0.75 m the boundary
 * did, which leaves the near end of the curve exactly where it was
 * photographed (and, more to the point, where it has to be -- another 0.75 m
 * of bed at the house end would have the mulch and its paver kerb standing on
 * the patio).  `depthFar` did not, so the far end of the bed widens by that
 * 0.75 m: the fruit tree and the shrubs down there had to come in off the
 * fence with the planting line, and they have to come in onto mulch rather
 * than onto the middle of a lawn laid last week.
 */
export const BED_INSET = 0.2;    // mulch stops this far short of the boards

export const SIDE_BED = {
  minX: LOT_MIN_X + BED_INSET,
  maxZ: -12.5,
  minZ: -27.5,
  depthNear: 1.55,      // it bulges out near the house and narrows away
  depthFar: 1.25,
};

/** Back border in front of the conifer screen. */
export const BACK_BED = { minZ: -29.6, maxZ: -27.4 };

/**
 * Routes other modules already rely on.  Anything built here must leave these
 * clear or the cat gets locked in or out of the garden.
 *
 * Every one of these is somewhere ANOTHER module builds, so every coordinate
 * is derived from whatever that module derives it from, and where it cannot
 * be, the comment says which module owns the number.  Two of them had drifted
 * off the thing they name: the board gap was listed at x -7.5 while fences.js
 * has always cut it at LOT_EDGES[1], which left the circle that is supposed
 * to protect that route sitting 0.75 m past it inside lot 0; and the side
 * gate was listed at 6.4, the x of the marker fences.js drops on the ramp,
 * rather than at the middle of the passage the gate hangs across.
 */
export const KEEP_CLEAR = [
  // The rear gate is gone, and with it the reason this list ever named a
  // single point on the back boundary.  The fence there is chain link at
  // 0.84 now -- inside a plain jump along its whole length -- so the route
  // out is the entire rear line rather than one opening, and there is no
  // circle that protects it.  What still has to hold is that the screen does
  // not grow THROUGH the fence, and that is enforced by plantLineX, not here.
  // fences.js keeps `route:rear-fence-hop` on the rail as the tripwire for
  // the boundary quietly becoming a wall again.
  // The side passage, between the house's garage-side wall and the boundary
  // fence; fences.js hangs the gate across it at its Z_GATE, ajar, and the
  // leaf swings back along the fence into the garden.
  { name: "side gate to front garden",
    x: (LOTS[HERO_LOT].house.maxX + LOT_MAX_X) / 2, z: -10.45, r: 1.3 },
  // fences.js boardGapBay on the lot0/lot1 boundary: the missing boards are
  // at z -19.48..-19.12, and the gap is in the fence, so it is ON the edge.
  { name: "board gap to lot 0", x: LOT_MIN_X, z: -19.3, r: 1.1 },
  // yards.js sideProps stands two wheelie bins and a recycle box in a row
  // 0.37 m off the same wall; the row runs from z -8.61 to -10.76, so this is
  // its midpoint and a radius that covers all three.
  { name: "bins in the side passage",
    x: LOTS[HERO_LOT].house.maxX + 0.37, z: -9.69, r: 1.1 },
];

export const clearOf = (x, z, pad = 0) =>
  KEEP_CLEAR.every((k) => Math.hypot(x - k.x, z - k.z) > k.r + pad);
